import React, { useState } from "react";
import {
  DataTable,
  DataTableColumn,
  FilterBar,
  FiltersContainer,
  SearchBar,
  Switch,
  Table,
  hasActiveFilters,
} from "@sscs/ui";
import { getClassName } from "@sscs/utils";
import { SharedReportLinkRecipient } from "../../../../../types";
import RecipientInfo from "./RecipientInfo";
import styles from "./recipientsTable.module.scss";
import { RecipientsTableFilter, filterRecipients, initFilter } from "./utils";

const COLUMNS: DataTableColumn[] = [{ id: "info", width: "100%" }];

type RecipientRowProps = {
  recipient: SharedReportLinkRecipient;
};

const RecipientRow = ({ recipient }: RecipientRowProps): React.JSX.Element => {
  return (
    <Table.Row>
      <Table.Cell>
        <RecipientInfo recipient={recipient} />
      </Table.Cell>
    </Table.Row>
  );
};

type RecipientsTableProps = {
  recipients: SharedReportLinkRecipient[];
  tableContainerClassName?: string;
};

const RecipientsTable = ({
  recipients,
  tableContainerClassName,
}: RecipientsTableProps): React.JSX.Element => {
  const [filter, setFilter] = useState<RecipientsTableFilter>(initFilter);

  const filteredRecipients = filterRecipients(recipients, filter);

  return (
    <div className={styles.container}>
      <FiltersContainer
        found={{
          id: "recipients-clear-filters",
          count: filteredRecipients.length,
          initialFilters: initFilter,
          activeFilters: filter,
          onClear: () => setFilter(initFilter),
        }}
      >
        <FilterBar
          stacking="never"
          filters={[
            {
              id: "email",
              control: (
                <SearchBar
                  id="search-recipient-email"
                  value={filter.email ?? ""}
                  onChange={(value) =>
                    setFilter({
                      ...filter,
                      email: value,
                    })
                  }
                  variant="outlined"
                  label="Search for E-mail"
                  slotProps={{ htmlInput: { maxLength: 256 } }}
                  debounceTimer={250}
                />
              ),
            },
            {
              id: "not-viewed",
              fit: true,
              control: (
                <Switch
                  title="Filter recipients"
                  label="Not viewed"
                  checked={filter.not_viewed_report_only}
                  onChange={(event) => {
                    setFilter({
                      ...filter,
                      not_viewed_report_only: event.target.checked,
                    });
                  }}
                  className={styles.label}
                />
              ),
            },
          ]}
        />
      </FiltersContainer>
      <div
        className={getClassName(
          styles["table-scroll"],
          tableContainerClassName,
        )}
      >
        <DataTable
          chrome="plain"
          hideHeader
          columns={COLUMNS}
          empty={{
            subject: "recipients",
            filters: {
              active: hasActiveFilters(initFilter, filter),
              onReset: () => setFilter(initFilter),
            },
          }}
        >
          {filteredRecipients.map((recipient) => (
            <RecipientRow key={recipient.email} recipient={recipient} />
          ))}
        </DataTable>
      </div>
    </div>
  );
};

export default RecipientsTable;
